import React, { useState } from 'react';
import { Card } from '../ui/Card.tsx';
import { Button } from '../ui/Button.tsx';
import { AlertTriangle, Trash2, Shield, X } from 'lucide-react';
import { deleteUserAccount, validateUserAction } from '../../services/accountService.ts';
import type { UserProfile } from '../../hooks/useUserProfile.ts';

interface DangerZoneProps {
  profile: UserProfile;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  userEmail?: string;
  loading: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  userEmail,
  loading
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const expectedText = 'DELETE MY ACCOUNT';
  const isConfirmationValid = confirmationText === expectedText;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-red-600 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            Delete Account
          </h3>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <h4 className="text-sm font-medium text-red-800 mb-2">
              This action cannot be undone
            </h4>
            <div className="text-sm text-red-700 space-y-1">
              <p>• Your account will be permanently deleted</p>
              <p>• All scan history and results will be removed</p>
              <p>• Your subscription will be cancelled</p>
              <p>• All personal data will be permanently erased</p>
            </div>
          </div>

          {userEmail && (
            <div className="text-sm text-gray-600">
              <p>Account to be deleted: <strong>{userEmail}</strong></p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="font-mono bg-gray-100 px-1 rounded">DELETE MY ACCOUNT</span> to confirm:
            </label>
            <input
              type="text"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Type the confirmation text"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!isConfirmationValid || loading}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {loading ? 'Deleting...' : 'Delete Account'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const DangerZone: React.FC<DangerZoneProps> = ({ 
  profile, 
  onSuccess, 
  onError 
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);

  console.log('[DangerZone] Rendering with profile:', {
    hasUser: !!profile.user,
    loading: profile.loading,
  });

  const handleDeleteRequest = async () => {
    console.log('[DangerZone] Delete account requested');
    
    // Validate user can perform this action
    const canDelete = await validateUserAction();
    if (!canDelete) {
      onError?.('Unable to validate user session. Please sign in again.');
      return;
    }

    setShowConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    console.log('[DangerZone] Delete account confirmed, proceeding...');
    setLoading(true);
    
    try {
      await deleteUserAccount();
      console.log('[DangerZone] Account deletion successful');
      onSuccess?.();
      // User will be signed out and redirected by the deleteUserAccount function
    } catch (err) {
      console.error('[DangerZone] Account deletion failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete account';
      onError?.(errorMessage);
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  if (profile.loading) {
    return (
      <Card className="mt-6 p-6 border-red-200">
        <div className="h-6 bg-gray-200 rounded-md w-32 mb-4 animate-pulse"></div>
        <div className="h-4 bg-gray-200 rounded-md w-full mb-2 animate-pulse"></div>
        <div className="h-4 bg-gray-200 rounded-md w-3/4 mb-4 animate-pulse"></div>
        <div className="h-10 bg-gray-200 rounded-md w-48 animate-pulse"></div>
      </Card>
    );
  }

  if (!profile.user) {
    return null; // Don't show danger zone if not logged in
  }

  return (
    <>
      <Card className="mt-6 p-6 border-red-700/50 bg-red-900/20">
        <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          Danger Zone
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <Shield className="h-5 w-5 text-red-400 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-300">Delete Account</h4>
              <p className="text-sm text-red-200 mt-1">
                Permanently delete your account and all associated data. This action cannot be undone. 
                All scans, results, and subscription data will be permanently removed.
              </p>
            </div>
          </div>

          <div className="pl-8">
            <Button
              onClick={handleDeleteRequest}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white flex items-center"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete My Account
            </Button>
          </div>

          <div className="mt-4 p-3 bg-red-900/30 rounded-lg border border-red-700/50">
            <div className="text-xs text-red-300 space-y-1">
              <p><strong>GDPR Compliance:</strong> Account deletion will permanently remove all personal data</p>
              <p><strong>Data Retention:</strong> No personal information will be retained after deletion</p>
              <p><strong>Subscriptions:</strong> Active subscriptions will be cancelled immediately</p>
            </div>
          </div>
        </div>
      </Card>

      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleDeleteConfirm}
        userEmail={profile.user.email}
        loading={loading}
      />
    </>
  );
}; 